import { db, storage } from '../lib/firebase';
import {
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  where,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  setDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { User } from '../types';
import { GoogleGenerativeAI } from '@google/generative-ai';

export interface ChatRoom {
  id: string;
  name: string;
  type: 'direct' | 'group' | 'ai';
  members: string[];
  lastMessage?: {
    content: string;
    timestamp: string;
    sender: string;
  };
  createdAt: Date;
}

export interface ChatMessage {
  id: string;
  content: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  workspaceId: string;
  chatRoomId: string;
  timestamp: Date;
  isAI?: boolean;
  attachments?: Array<{
    type: 'file' | 'image';
    url: string;
    name: string;
  }>;
}

export interface OnlineStatus {
  online: boolean;
  lastSeen: Date;
}

const AI_USER = {
  id: 'ai-assistant',
  name: 'eGemini AI',
  avatar: '/ai-avatar.svg'
};

const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY);

export const chatService = {
  async getChatRooms(workspaceId: string): Promise<ChatRoom[]> {
    try {
      const roomsRef = collection(db, 'workspaces', workspaceId, 'chatRooms');
      const q = query(roomsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ChatRoom[];
    } catch (error) {
      console.error('Error getting chat rooms:', error);
      throw error;
    }
  },

  async getMessages(workspaceId: string, chatRoomId: string, messageLimit = 50): Promise<ChatMessage[]> {
    try {
      const messagesRef = collection(db, 'workspaces', workspaceId, 'chatRooms', chatRoomId, 'messages');
      const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(messageLimit));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      })) as ChatMessage[];
    } catch (error) {
      console.error('Error getting messages:', error);
      throw error;
    }
  },

  subscribeToMessages(workspaceId: string, chatRoomId: string, callback: (message: ChatMessage) => void): () => void {
    const messagesRef = collection(db, 'workspaces', workspaceId, 'chatRooms', chatRoomId, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(1));
    
    return onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const message = {
            id: change.doc.id,
            ...change.doc.data(),
            timestamp: change.doc.data().timestamp?.toDate()
          } as ChatMessage;
          callback(message);
        }
      });
    });
  },

  async sendMessage(workspaceId: string, message: Omit<ChatMessage, 'id' | 'timestamp'>): Promise<string> {
    try {
      const messagesRef = collection(db, 'workspaces', workspaceId, 'chatRooms', message.chatRoomId, 'messages');
      const docRef = await addDoc(messagesRef, {
        ...message,
        timestamp: serverTimestamp()
      });

      // Update last message in chat room
      const roomRef = doc(db, 'workspaces', workspaceId, 'chatRooms', message.chatRoomId);
      await updateDoc(roomRef, {
        lastMessage: {
          content: message.content,
          timestamp: new Date().toISOString(),
          sender: message.userName
        }
      });

      return docRef.id;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  async uploadAttachment(file: File, workspaceId: string): Promise<{ type: 'file' | 'image'; url: string; name: string }> {
    try {
      const fileRef = ref(storage, `workspaces/${workspaceId}/chat-attachments/${Date.now()}-${file.name}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

    return {
        type: file.type.startsWith('image/') ? 'image' : 'file',
        url,
        name: file.name
      };
    } catch (error) {
      console.error('Error uploading attachment:', error);
      throw error;
    }
  },

  async createDirectChat(workspaceId: string, members: string[], name: string): Promise<string> {
    try {
      const roomsRef = collection(db, 'workspaces', workspaceId, 'chatRooms');
      const docRef = await addDoc(roomsRef, {
        type: 'direct',
        members,
        name,
        createdAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating direct chat:', error);
      throw error;
    }
  },

  async createGroupChat(workspaceId: string, members: string[], name: string): Promise<string> {
    try {
      const roomsRef = collection(db, 'workspaces', workspaceId, 'chatRooms');
      const docRef = await addDoc(roomsRef, {
        type: 'group',
        members,
        name,
        createdAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating group chat:', error);
      throw error;
    }
  },

  async getWorkspaceUsers(workspaceId: string): Promise<User[]> {
    try {
      const usersRef = collection(db, 'workspaces', workspaceId, 'users');
      const snapshot = await getDocs(usersRef);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as User[];
    } catch (error) {
      console.error('Error getting workspace users:', error);
      throw error;
    }
  },

  async updateOnlineStatus(workspaceId: string, userId: string, online: boolean): Promise<void> {
    try {
      const statusRef = doc(db, 'workspaces', workspaceId, 'users', userId, 'status', 'online');
      await setDoc(statusRef, {
        online,
        lastSeen: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating online status:', error);
      throw error;
    }
  },

  subscribeToOnlineStatus(workspaceId: string, userId: string, callback: (status: OnlineStatus) => void): () => void {
    const statusRef = doc(db, 'workspaces', workspaceId, 'users', userId, 'status', 'online');
    return onSnapshot(statusRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        callback({
          online: data.online,
          lastSeen: data.lastSeen?.toDate() || new Date()
        });
      }
    });
  },

  async getOnlineUsers(workspaceId: string): Promise<string[]> {
    try {
      const usersRef = collection(db, 'workspaces', workspaceId, 'users');
      const snapshot = await getDocs(usersRef);
      const onlineUsers: string[] = [];

      for (const userDoc of snapshot.docs) {
        const statusRef = doc(db, 'workspaces', workspaceId, 'users', userDoc.id, 'status', 'online');
        const statusDoc = await getDoc(statusRef);
        if (statusDoc.exists() && statusDoc.data().online) {
          onlineUsers.push(userDoc.id);
        }
      }

      return onlineUsers;
    } catch (error) {
      console.error('Error getting online users:', error);
      throw error;
    }
  },

  async getWorkspaceMembers(workspaceId: string): Promise<User[]> {
    try {
      const membersRef = collection(db, 'workspaces', workspaceId, 'members');
      const q = query(membersRef, where('status', '==', 'active'));
      const snapshot = await getDocs(q);
      
      const members: User[] = [];
      const userPromises = snapshot.docs.map(async (memberDoc) => {
        const userRef = doc(db, 'users', memberDoc.data().userId);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          return {
            id: userDoc.id,
            ...userDoc.data()
          } as User;
        }
        return null;
      });

      const resolvedUsers = await Promise.all(userPromises);
      return resolvedUsers.filter((user): user is User => user !== null);
    } catch (error) {
      console.error('Error getting workspace members:', error);
      throw error;
    }
  },

  async createAIChat(workspaceId: string, userId: string): Promise<string> {
    try {
      const roomsRef = collection(db, 'workspaces', workspaceId, 'chatRooms');
      
      // Check if AI chat already exists for this user
      const q = query(roomsRef, 
        where('type', '==', 'ai'),
        where('members', 'array-contains', userId)
      );
      const existingChats = await getDocs(q);
      
      if (!existingChats.empty) {
        return existingChats.docs[0].id;
      }

      const docRef = await addDoc(roomsRef, {
        type: 'ai',
        members: [userId],
        name: 'eGemini AI',
        createdAt: serverTimestamp(),
        aiConfig: {
          model: 'gemini-2.0-flash',
          temperature: 0.7,
          maxTokens: 1000
        }
      });

      // Add welcome message
      const messagesRef = collection(db, 'workspaces', workspaceId, 'chatRooms', docRef.id, 'messages');
      await addDoc(messagesRef, {
        content: "Hello! I'm eGemini AI, powered by Google's Gemini Pro model. I can help you with:\n\n" +
                "• Analyzing business data and providing insights\n" +
                "• Answering questions about invoices and payments\n" +
                "• Helping with client communication and email drafts\n" +
                "• Providing suggestions for workspace optimization\n" +
                "• Assisting with technical and business queries\n\n" +
                "Feel free to ask me anything! I'll do my best to help.",
        userId: AI_USER.id,
        userName: AI_USER.name,
        userAvatar: AI_USER.avatar,
        workspaceId,
        chatRoomId: docRef.id,
        timestamp: serverTimestamp(),
        isAI: true
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating AI chat:', error);
      throw error;
    }
  },

  async sendMessageToAI(workspaceId: string, chatRoomId: string, message: Omit<ChatMessage, 'id' | 'timestamp'>): Promise<void> {
    try {
      // First, save the user's message
      const messagesRef = collection(db, 'workspaces', workspaceId, 'chatRooms', chatRoomId, 'messages');
      await addDoc(messagesRef, {
        ...message,
        timestamp: serverTimestamp()
      });

      // Get AI response using Gemini
      const model = genAI.getGenerativeModel({ model: "gemini-2.0-flash" });
      const result = await model.generateContent(message.content);
      const response = await result.response;
      const aiResponseText = response.text();

      // Save the AI's response
      const aiResponse = {
        content: aiResponseText,
        userId: AI_USER.id,
        userName: AI_USER.name,
        userAvatar: AI_USER.avatar,
        workspaceId,
        chatRoomId,
        isAI: true
      };

      await addDoc(messagesRef, {
        ...aiResponse,
        timestamp: serverTimestamp()
      });

      // Update last message in chat room
      const roomRef = doc(db, 'workspaces', workspaceId, 'chatRooms', chatRoomId);
      await updateDoc(roomRef, {
        lastMessage: {
          content: aiResponseText,
          timestamp: new Date().toISOString(),
          sender: AI_USER.name
        }
      });
    } catch (error) {
      console.error('Error sending message to AI:', error);
      
      // Save error message if AI fails
      const messagesRef = collection(db, 'workspaces', workspaceId, 'chatRooms', chatRoomId, 'messages');
      const errorResponse = {
        content: "I apologize, but I encountered an error. Please try again or check the API key configuration.",
        userId: AI_USER.id,
        userName: AI_USER.name,
        userAvatar: AI_USER.avatar,
        workspaceId,
        chatRoomId,
        isAI: true
      };

      await addDoc(messagesRef, {
        ...errorResponse,
        timestamp: serverTimestamp()
      });

      // Update last message to show error
      const roomRef = doc(db, 'workspaces', workspaceId, 'chatRooms', chatRoomId);
      await updateDoc(roomRef, {
        lastMessage: {
          content: errorResponse.content,
          timestamp: new Date().toISOString(),
          sender: AI_USER.name
        }
      });

      throw error;
    }
  }
}; 